import React, { use, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "hooks/useAuth";
import { useToast } from "hooks/useToast";
import { AuthLayout } from "components/layout/AuthLayout";
import { Input, Button, Checkbox, Card, CardBody } from "components/ui";
import { LoginCredentials } from "types/index";
import { validators } from "utils/validators";
import { ROUTES } from "constants/index";
import logo from "../../assets/images/logo.png";
import { ArrowBack } from "@mui/icons-material";
import authService from "@/services/auth.service";
import { tokenStorage } from "utils/storage";

export const LoginPage: React.FC = () => {
  const {
    reset,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginCredentials>();

  const { login, forgotPassword } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isforgotPassword, setIsForgotPassword] = useState(false);
  const handleForgotPassword = () => {
    reset();
    setIsForgotPassword(true);
  };

  const onSubmit = async (data: LoginCredentials) => {
    setIsLoading(true);
    if(isforgotPassword){
      try{
       await forgotPassword(data.email);
      }
      catch(error){
        toast.error("Failed to send password reset link.");
      }finally{
      setIsLoading(false);}
    }else
    try {
      var result = await login(data);
      
      // Check if access token exists and is not empty (indicates successful login with token)
      const accessToken = tokenStorage.getAccessToken();
      const hasValidToken = accessToken !== null && accessToken !== "";
      
      if (!hasValidToken) {
        // No access token - check user state and redirect accordingly
        if (sessionStorage.getItem("user")) {
          sessionStorage.removeItem("user");
        }
        
        // Account with userId null: must continue registration (do not go to backoffice)
        const redirectToRegister = (result as any)?.redirect_to_register === true;
        if (redirectToRegister) {
          const step = (result as any)?.step ?? 1;
          navigate(`${ROUTES.REGISTER}?userId=${result.id}&step=${step}`, { replace: true });
          return;
        }
        
        const isCodeRequired = (result as any)?.isCodeRequired ?? false;
        const isActive = (result as any)?.isActive ?? false;
        const hasCompany = !!(result as any)?.company_id;
        
        // If code is required, redirect to register for verification step (step 2)
        if (isCodeRequired) {
          navigate(`${ROUTES.REGISTER}?userId=${result.id}&step=2`, { replace: true });
        }
        // If not activated, redirect to payment step (step 3)
        else if (!isActive) {
          navigate(`${ROUTES.REGISTER}?userId=${result.id}&step=3`, { replace: true });
        }
        // If activated but no company, redirect to register to create company (step 4)
        else if (!hasCompany) {
          navigate(`${ROUTES.REGISTER}?userId=${result.id}&step=4`, { replace: true });
        }
        // Fallback to register with userId
        else {
          navigate(`${ROUTES.REGISTER}?userId=${result.id}`, { replace: true });
        }
      } else {
        // Has access token - store data and redirect to dashboard
        if (sessionStorage.getItem("user")) {
          sessionStorage.removeItem("user");
        }
        toast.success("Login successful! Welcome back.");
        navigate(ROUTES.DASHBOARD, { replace: true });
      }
    } catch (error: any) {
      toast.error(
        error?.message || "Login failed. Please check your credentials.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout>
      <Card>
        <CardBody className="p-4 sm:p-6">
          <div className="text-center mb-6 sm:mb-8">
            <Link to={ROUTES.HOME} className="flex items-center gap-2">
              <img
                src={logo}
                alt="App Logo"
                className="mx-auto mb-3 sm:mb-4 w-32 h-32 sm:w-40 sm:h-40 object-contain"
              />
            </Link>
          </div>
          {isforgotPassword && (
            <h2 className="text-primary-700 font-large font-bold text-center mb-4 sm:mb-6">
              Please enter your email to proceed with the password recovery process</h2>)}
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-3 sm:space-y-4"
          >
            <Input
              label="Email"
              type="text"
              placeholder="Enter your email"
              fullWidth
              autoFocus
              error={errors.email?.message}
              leftIcon={
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                  />
                </svg>
              }
              {...register("email", { required: validators.required("email") })}
            />
            {isforgotPassword ? (
              <>
                <Button
                  variant="primary"
                  size="md"
                  className="mt-8 sm:mt-12"
                  fullWidth
                  isLoading={isLoading}
                  disabled={isLoading}
                  type="submit"
                >
                  Send Reset Link
                </Button>
                <div className="flex items-center justify-center">
                <ArrowBack fontSize="small" className="mr-2"/>
                <Link to={"/login"} onClick={()=>{setIsForgotPassword(false)}} className="text-primary-700 font-medium">
                  Back to Sign In
                </Link>
              </div>
              </>
            ) : (
              <>
                <Input
                  label="Password"
                  type="password"
                  placeholder="Enter your password"
                  fullWidth
                  error={errors.password?.message}
                  leftIcon={
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                  }
                  {...register("password", {
                    required: validators.required("password"),
                    validate: validators.minLength(6),
                  })}
                />

                <div className="flex items-center justify-end">
                  {/* <Checkbox label="Remember me" {...register('rememberMe')} /> */}
                  <Link
                    to={""}
                    onClick={handleForgotPassword}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Forgot password?
                  </Link>
                </div>
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="mt-6 sm:mt-8"
                  fullWidth
                  isLoading={isLoading}
                  disabled={isLoading}
                >
                  Sign In
                </Button>
          <div className="mt-4 sm:mt-6 text-center">
            <p className="text-xs sm:text-sm text-gray-600">
              Don't have an account?{" "}
              <Link
                to={ROUTES.PRICING}
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                Get Started
              </Link>
            </p>
          </div>
              </>
            )}
          </form>


          {/* <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-secondary-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-secondary-500">Or continue with</span>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-3">
              <Button variant="outline" size="md" type="button">
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z" />
                </svg>
                <span className="ml-2">Google</span>
              </Button>
            </div>
          </div> */}
        </CardBody>
      </Card>
    </AuthLayout>
  );
};
